import { useRuns } from '../../api/hooks';
import { SOURCE_LABELS } from '../../api/types';
import { Badge } from '../../components/Badge';
import { Card, CardHeader } from '../../components/Card';
import { EmptyState, QueryBoundary } from '../../components/states';
import { useFormatters } from '../../time/format';
import { formatDuration } from '../../time/useNow';

interface RunHistoryProps {
  enabled: boolean;
  /** Empty for every pollable source interleaved. */
  source: string;
}

/**
 * What the table is showing, and — once someone has paged back — that it has stopped moving.
 *
 * The rest of this page refreshes itself every fifteen seconds, so a table that had quietly
 * frozen would read as "no polls have run since", which is the opposite of what it means.
 */
function subtitle({
  label,
  count,
  paged,
}: {
  label: string;
  count: number;
  paged: boolean;
}): string {
  const scope = label || 'Every pollable source';
  if (count === 0) return `${scope} · kept for seven days`;
  const polls = `${count} ${count === 1 ? 'poll' : 'polls'}`;
  return paged
    ? `${scope} · ${polls} · paused while you page`
    : `${scope} · ${polls} · kept for seven days`;
}

/**
 * Recent polls, newest first.
 *
 * The question the cards above cannot answer. They say whether each source is alive right
 * now; this says whether it is behaving as it did yesterday, and the two come apart in the
 * cases that matter. A source whose polls have gone from one page to forty, or from 200ms to
 * two minutes, reports `ok` on every card and is in trouble — Red Canary once spent a poll
 * making 287 identical requests until its time budget ran out and reported success, and a
 * page count in a column would have made that obvious at a glance.
 *
 * A week deep. These rows carry a TTL because they are operational telemetry rather than the
 * incident record, which is the distinction that decides what expires in this table.
 */
export function RunHistory({ enabled, source }: RunHistoryProps) {
  const runs = useRuns(enabled, source);
  const { dateTime, precise } = useFormatters();

  // Concatenated, not re-sorted. The server returns a total order over
  // `(started_at desc, source asc)` and each page starts strictly after the previous one
  // ended, so appending preserves it. Re-sorting here on `started_at` alone would discard
  // the source tiebreak and shuffle rows that share a millisecond — which, since every
  // schedule fires on the same rate expression, is a whole tick's worth of them.
  const rows = runs.data?.pages.flatMap((page) => page.runs) ?? [];
  const label = source ? (SOURCE_LABELS[source as keyof typeof SOURCE_LABELS] ?? source) : '';
  const paged = (runs.data?.pages.length ?? 0) > 1;

  return (
    <Card className="p-0">
      <div className="p-5 pb-0">
        <CardHeader
          title="Run history"
          subtitle={subtitle({ label, count: rows.length, paged })}
        />
      </div>

      <QueryBoundary
        isLoading={runs.isPending}
        error={runs.error}
        onRetry={() => void runs.refetch()}
        loadingLabel="Loading run history"
      >
        {rows.length === 0 ? (
          <div className="p-5 pt-0">
            <EmptyState
              title="No polls recorded yet"
              detail="Runs appear here once the scheduler has fired. On a fresh deployment that is within a few minutes."
            />
          </div>
        ) : (
          <div className="scroll-thin overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-line text-xs uppercase tracking-wide text-subtle">
                <tr>
                  <th scope="col" className="px-5 py-3 font-semibold">
                    Started
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold">
                    Source
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold">
                    Result
                  </th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold">
                    Duration
                  </th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold">
                    Ingested
                  </th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold">
                    Pages
                  </th>
                  <th scope="col" className="px-5 py-3 font-semibold">
                    Trigger
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((run) => (
                  <tr key={`${run.source}-${run.started_at}`}>
                    <td
                      className="whitespace-nowrap px-5 py-2.5 text-xs text-muted"
                      title={precise(run.started_at)}
                    >
                      {dateTime(run.started_at)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted">
                      {SOURCE_LABELS[run.source] ?? run.source}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={run.ok ? 'success' : 'danger'}>{run.ok ? 'ok' : 'failed'}</Badge>
                      {run.error ? (
                        <span className="ml-2 font-mono text-[11px] text-danger-fg">
                          {run.error}
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs tabular-nums text-muted">
                      {formatDuration(run.duration_ms)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums text-muted">
                      {run.ingested === 0 ? <span className="text-subtle">—</span> : run.ingested}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums text-muted">
                      {run.pages}
                    </td>
                    <td className="px-5 py-2.5 text-xs text-muted">
                      {/*
                        A manual run explains a gap in the scheduled cadence that would
                        otherwise read as a missed tick, which is why the distinction is
                        recorded rather than inferred from the timing.
                      */}
                      {run.trigger === 'scheduled' ? (
                        <span className="text-subtle">scheduled</span>
                      ) : (
                        <Badge tone="info">{run.trigger || 'unknown'}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {runs.hasNextPage ? (
              <div className="border-t border-line p-4 text-center">
                <button
                  type="button"
                  onClick={() => void runs.fetchNextPage()}
                  disabled={runs.isFetchingNextPage}
                  className="rounded-lg border border-line px-4 py-1.5 text-xs font-medium text-muted transition-colors hover:border-accent hover:text-fg disabled:cursor-wait disabled:opacity-60"
                >
                  {runs.isFetchingNextPage ? 'Loading…' : 'Load older polls'}
                </button>
              </div>
            ) : (
              /*
                Said explicitly rather than by the absence of a button. In a history that
                expires, running out of rows and failing to load them look identical, and an
                operator scrolling for the change that broke a source needs to know which
                one they are looking at.
              */
              <p className="border-t border-line p-4 text-center text-xs text-subtle">
                {rows.length > 0 ? 'Start of the retained history' : null}
              </p>
            )}
          </div>
        )}
      </QueryBoundary>
    </Card>
  );
}
